import { useQuery } from '@tanstack/react-query';

import { getLineItemStatus, LineItemStatus } from '../api/ventura/lineItem';
import { useConfig } from './useConfig';

// Fetches a line item's (IO's) BigQuery-backed delivery status (ADOPS-2733), keyed by id.
// This is a supplementary signal on top of the daily spend table, so on ANY failure it degrades
// to `null` and the caller simply renders nothing — a status outage must not take the Daily Spend
// Activity modal down with it. `null` and not `undefined`: react-query treats an undefined
// resolution as a failed query, which would push this into an error state, log on its own account,
// and re-fetch on every modal open instead of honouring staleTime. Cached for a minute: the
// underlying events_streaming_view is near-real-time, but ventura caches the query for 5 minutes
// anyway.
function useLineItemStatus(lineItemId?: number) {
  const { apiHost } = useConfig();

  return useQuery<LineItemStatus | null>({
    queryKey: ['lineItemStatus', apiHost, lineItemId],
    queryFn: async () => {
      try {
        return await getLineItemStatus(apiHost, lineItemId as number);
      } catch (err) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn('useLineItemStatus: status fetch failed; hiding status rows', err);
        }
        return null;
      }
    },
    retry: false,
    staleTime: 60_000,
    enabled: !!lineItemId,
  });
}

export default useLineItemStatus;
